import React from 'react';
import { useDepartment } from '../contexts/DepartmentContext';
import { useAuth } from '../contexts/AuthContext';
import { shouldWarnOnSwitch } from '../utils/personalSpace';
import ConfirmDialog from './ConfirmDialog';
import JoinDepartmentDialog, { UNSAVED_SWITCH_TITLE, UNSAVED_SWITCH_MESSAGE, toBody } from './JoinDepartmentDialog';

import { ROLE_LABELS } from '../constants/statuses';
import { spaceLabelFor } from '../utils/spaceLabel';
import { collidingIds, withIdSuffix } from '../utils/duplicateLabels';

// 🧹 FOLLOW-UP FIX (2026-08-31) — HOISTED. Was a verbatim second copy: the
// "All Productions" row's shell/active-state classes and the ordinary
// department row's were typed out twice, so retuning one (a hover shade, the
// active border) would silently leave the other behind. ONE definition, read
// by both option-row renders below.
const OPTION_ROW_BASE = 'w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors';
const OPTION_ROW_ACTIVE = 'bg-blue-50 border-l-2 border-blue-500 dark:bg-blue-600/30 dark:border-blue-400';
const OPTION_ROW_INACTIVE = 'hover:bg-gray-100 dark:hover:bg-white/10 border-l-2 border-transparent';
const optionRowClassName = (isActive) => `${OPTION_ROW_BASE} ${isActive ? OPTION_ROW_ACTIVE : OPTION_ROW_INACTIVE}`;

// 🆕 A PENDING-INVITE ROW (join-a-second-department, P5). The TEXT line is the
// carrier, never colour alone: an invite must read as "not yet yours" to
// anyone, so it says so in words. No WaitingDot (nothing waits on you there)
// and no RoleBadge (you hold no role there yet — the offered role is in the
// Join prompt).
export const PENDING_INVITE_TEXT = 'Pending invite — click to join';
const PENDING_ROW_CLASS = `${OPTION_ROW_BASE} ${OPTION_ROW_INACTIVE} border-t border-t-gray-100 dark:border-t-white/10`;

// The label a pending row renders. Same "<production> - <department>" shape as
// a real row, so the two lists read as one.
export function pendingInviteLabel(invite) {
  return `${invite.production_name || 'Unknown'} - ${invite.department_name || 'Unknown'}`;
}

// ── The WAITING DOT (spec 2026-09-01) ──────────────────────────────────────
// "Something in that department is waiting on YOU" — an order at your
// approval step, or your own order returned rejected. Deliberately NOT
// "activity": the urgency-summary deptOrders bucket is excluded by design.
// The count lives in the TOOLTIP, never on the dot (owner ruling — the
// switcher is persistent chrome and a number on it is noise).
// ♿ SOLE colour carrier → WCAG 1.4.11 3:1 + a non-colour channel. The token
// is the ALREADY-MEASURED pair from Layout.urgencyDotContrast.test.js
// (red-500 3.76:1 light / red-400 5.29:1 on the REAL dark #1e293b — index.css
// forces .bg-white/.bg-gray-50 there with !important); every surface this
// component's dot actually sits on (trigger bg-gray-50, dropdown active row
// bg-blue-50, and their dark composites) is measured in
// ProductionDepartmentSwitcher.waitingDot.test.js — tightest 3.42:1.
// Re-measure before changing the shade — amber-500 is a measured FAIL (2.15:1).
export const WAITING_DOT_CLASS = 'inline-block h-2.5 w-2.5 rounded-full bg-red-500 dark:bg-red-400 shrink-0';

// One exported builder per tooltip so the visible title and the sr-only copy
// are fed from ONE string and can never drift — the components/InactiveBadge.js
// device (see also budget/CrewDayGrid.js, budget/CrewGanttView.js).
export function buildWaitingTooltip(entries) {
  const nonZero = (entries || []).filter(e => e.count > 0);
  const total = nonZero.reduce((s, e) => s + e.count, 0);
  if (total === 0) return '';
  return `${total} waiting: ${nonZero.map(e => `${e.count} in ${e.label}`).join(', ')}`;
}

export function rowWaitingTooltip(count) {
  return `${count} waiting`;
}

// title + sr-only from the ONE `tip` string. No aria-label / role="img" —
// that is DueIndicator's different device; do not merge the two.
function WaitingDot({ tip, testid }) {
  return (
    <span className={WAITING_DOT_CLASS} title={tip} data-testid={testid}>
      <span className="sr-only">{tip}</span>
    </span>
  );
}

// Role badge color mapping — light mode + dark mode
const ROLE_BADGE_COLORS = {
  admin: 'bg-red-100 text-red-700 dark:bg-red-500/30 dark:text-red-200',
  buyer: 'bg-orange-100 text-orange-700 dark:bg-orange-500/30 dark:text-orange-200',
  user: 'bg-blue-100 text-blue-700 dark:bg-blue-500/30 dark:text-blue-200',
  sub_department_lead: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/30 dark:text-yellow-200',
  supervisor: 'bg-teal-100 text-teal-700 dark:bg-teal-500/30 dark:text-teal-200',
};

// Render an assignment row. For personal spaces, return just the space label
// ("My Space" / "<name>'s Space") — the dept is always the same name and
// doubling it is noise. For normal productions, keep "<prod> - <dept>".
//
// `showId` appends the department id, and is passed ONLY for entries that
// collide with another of the user's own departments (see
// `collidingDepartmentIds`). It is deliberately conditional: a user with no
// collisions — nearly everyone — sees no ids anywhere and the switcher is
// exactly what it was.
function formatAssignment(a, authUser, showId = false) {
  const prodLabel = spaceLabelFor(
    {
      is_personal: a.production_is_personal,
      owner_user_id: a.production_owner_user_id,
      production_name: a.production_name,
      company_name: a.company_name,
    },
    authUser
  ) || 'Unknown';
  const deptName = a.department_name || 'Unknown';
  const label = a.production_is_personal ? prodLabel : `${prodLabel} - ${deptName}`;
  return showId ? withIdSuffix(label, a.department_id) : label;
}

/**
 * Which of the user's OWN departments share a department name with another?
 *
 * A department name is a human label — user-editable, and NOT unique. Two
 * departments called "Props" are legitimate and expected, INCLUDING two inside
 * the same production (product owner, 2026-08-05: *"so to confirm we could have
 * multiple departments with the same name in a single production? this is
 * unlikey to ever happen but it might"* — answered YES, and it must be
 * supported). In that case the production name distinguishes nothing at all,
 * and two same-named PRODUCTIONS make it useless in the ordinary case too.
 * The id is the right label precisely because it is unique by definition.
 *
 * Collision is on the DEPARTMENT NAME, not on the rendered label: the ruling is
 * scoped to *"the user's own list contains two or more departments sharing a
 * name"*.
 *
 * Scoped to the caller's own assignments — a display aid for one person's
 * dropdown, NOT a system-wide uniqueness mechanism. `department_id` remains the sole
 * identity in the data model (it is already what `handleSelect` and the
 * active-row check use); this only puts it on screen. And there is no duplicate
 * WARNING: *"no need for the UI to warn about it as each is its own island and
 * doesnt care about the other"*.
 */
export function collidingDepartmentIds(assignments) {
  return collidingIds(
    (assignments || []).map(a => ({ id: a.department_id, key: a.department_name }))
  );
}

function RoleBadge({ role }) {
  const label = ROLE_LABELS[role] || role;
  const colors = ROLE_BADGE_COLORS[role] || 'bg-gray-100 text-gray-700 dark:bg-gray-500/30 dark:text-gray-200';
  return (
    <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${colors}`}>
      {label}
    </span>
  );
}

// Building icon for department switcher
const BuildingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 dark:text-blue-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

// Chevron icon for dropdown indicator
const ChevronIcon = ({ open }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`h-3 w-3 text-gray-400 dark:text-blue-300 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

// Globe icon for admin "All Productions"
const GlobeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 dark:text-blue-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// 📱 `forceOpen` / `onDropdownClosed` (owner 2026-10-04: "how does someone on mobile switch
// productions? Clicking the production name at the top doesnt seem to bring up any drop down").
// On a phone this switcher's row is hidden until the header's production name is tapped; the
// header then opens the list through `forceOpen`, and hides the row again when the list closes
// (a choice, an outside tap or Escape). A tap on an element marked `data-dept-switcher-trigger`
// is not an "outside" tap, so the name can close what it opened. Both default to inert.
export default function ProductionDepartmentSwitcher({ compact = false, waitingByDepartment = {}, forceOpen = false, onDropdownClosed = null }) {
  const {
    assignments, activeDepartment, switchDepartment, hasDirtyEdit, isAdmin, loading,
    assignmentsError, pendingInvites
  } = useDepartment();
  // Never merged into `assignments` — see `DepartmentContext.js`. Defaulted
  // because several harnesses mock `useDepartment()` without it.
  const invites = Array.isArray(pendingInvites) ? pendingInvites : [];
  // The invite whose Join prompt is open. Null = no prompt. Deliberately
  // component state and nothing else: R16's "asked every time" is exactly
  // "never remembered".
  const [joinInvite, setJoinInvite] = React.useState(null);
  // Where focus goes when the Join prompt closes: the pending row that opened
  // it has unmounted by then (addendum C, 2026-09-24).
  const triggerRef = React.useRef(null);
  // An invite clicked while an order page held a dirty edit — the unsaved-
  // changes dialog opens first, and only Continue leads to the Join prompt.
  const [pendingDirtyJoin, setPendingDirtyJoin] = React.useState(null);
  const { user: authUser } = useAuth();
  const [isOpen, setIsOpen] = React.useState(false);
  // The personal space the user has asked for but not yet confirmed. Null
  // means no dialog — and, critically, means nothing has been switched yet.
  const [pendingPersonal, setPendingPersonal] = React.useState(null);
  // The department requested while an order page held a dirty inline edit,
  // awaiting the "you'll lose it" confirm below. Null means no dialog and
  // nothing switched — same contract as `pendingPersonal`. See
  // `DepartmentContext.js`'s `hasDirtyEdit` docblock for the full ruling.
  const [pendingDirtySwitch, setPendingDirtySwitch] = React.useState(null);
  const dropdownRef = React.useRef(null);

  // The subset of the user's departments whose NAME is ambiguous within their
  // own list. Empty for almost everyone, and when it is empty nothing below
  // renders any differently than it did.
  const ambiguousDeptIds = React.useMemo(
    () => collidingDepartmentIds(assignments),
    [assignments]
  );
  // 🔻 THE STALE-SENTINEL SHORT-CIRCUIT WAS REMOVED HERE ON 2026-08-31.
  // Task 1 left it deliberately, because `DepartmentContext.js` could still
  // honour a stored `all-my-departments` and hand this component a synthetic
  // `activeDepartment` carrying only `{ department_id }` — no
  // `production_name`, `department_name` or `role` — which `formatAssignment`
  // would have rendered as "Unknown - Unknown". Task 2 deleted the sentinel
  // from the context, so `activeDepartment` is now always `null` or a real
  // assignment row and that branch became dead. Its premise is gone; leaving
  // it would be a false claim in the code about what this component can be
  // handed.
  const label = (a) => formatAssignment(a, authUser, a && ambiguousDeptIds.has(a.department_id));

  // Per-assignment waiting counts, in the dropdown's own order, labelled by
  // the SAME `label()` the rows render — the tooltip can never disagree with
  // the list it points at (collision id-suffixes and personal-space labels
  // included). Missing key = 0; zero entries are dropped. Personal spaces are
  // present in `assignments` and simply never carry a count — no special case.
  // (Uses `formatAssignment(...)` directly rather than the `label` closure so
  // the dependency array below is honest; the output is identical.)
  const waitingEntries = React.useMemo(
    () => (assignments || [])
      .map(a => ({
        departmentId: a.department_id,
        label: formatAssignment(a, authUser, ambiguousDeptIds.has(a.department_id)),
        count: Number(waitingByDepartment[a.department_id]) || 0,
      }))
      .filter(e => e.count > 0),
    [assignments, waitingByDepartment, authUser, ambiguousDeptIds]
  );
  const collapsedTip = buildWaitingTooltip(waitingEntries);
  const waitingCountFor = (deptId) => {
    const e = waitingEntries.find(w => w.departmentId === deptId);
    return e ? e.count : 0;
  };

  React.useEffect(() => { setIsOpen(!!forceOpen); }, [forceOpen]);
  const wasOpenRef = React.useRef(false);
  const onDropdownClosedRef = React.useRef(onDropdownClosed);
  onDropdownClosedRef.current = onDropdownClosed;
  React.useEffect(() => {
    if (wasOpenRef.current && !isOpen && onDropdownClosedRef.current) onDropdownClosedRef.current();
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.closest && e.target.closest('[data-dept-switcher-trigger]')) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  if (loading) {
    return (
      <div className="px-3 py-2" data-testid="dept-switcher-loading">
        <div className="animate-pulse bg-gray-100 dark:bg-white/10 rounded-lg h-10"></div>
      </div>
    );
  }

  // 🚨 THE LOAD FAILED — SAY SO. It must be tested BEFORE every
  // `assignments.length === 0` branch below, because a failed
  // `GET /api/roles/my-assignments` leaves `assignments` empty and `isAdmin`
  // false, which is byte-for-byte the shape of a genuinely unseated user. The
  // branch below would then tell someone whose connection dropped that they
  // have "No department assigned" — a confident claim about their employment,
  // made from an unanswered question. See `assignmentsError` in
  // `contexts/DepartmentContext.js`.
  //
  // ⚠️ It sits AFTER the `loading` branch deliberately: while a retry is in
  // flight the honest thing on screen is the skeleton, not a stale error.
  if (assignmentsError) {
    return (
      <div className="px-3 py-2" data-testid="dept-switcher">
        <div className="flex items-center gap-2 bg-gray-50 dark:bg-white/5 rounded-lg px-3 py-2 border border-amber-300 dark:border-amber-500/40">
          <BuildingIcon />
          <p
            className={`${compact ? 'text-xs' : 'text-sm'} text-amber-700 dark:text-amber-300`}
            data-testid="dept-switcher-error"
          >
            {/* Typographic apostrophe, matching `Approvals.js`'s copy for the
                same failure — the two surfaces are read one after the other. */}
            {'Couldn’t load your departments — reload to try again'}
          </p>
        </div>
      </div>
    );
  }

  // Admin with no specific department selected - show "All Productions"
  if (isAdmin && assignments.length === 0) {
    return (
      <div className="px-3 py-2" data-testid="dept-switcher">
        <div className="flex items-center gap-2 bg-gray-50 dark:bg-white/5 rounded-lg px-3 py-2 border border-gray-200 dark:border-white/10">
          <GlobeIcon />
          <div className="min-w-0 flex-1">
            <p className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-800 dark:text-white truncate`}>All Productions</p>
            <p className="text-[10px] text-gray-500 dark:text-blue-300">Global Admin View</p>
          </div>
          <RoleBadge role="admin" />
        </div>
      </div>
    );
  }

  // No assignments and not admin - show nothing meaningful
  //
  // 🆕 …UNLESS AN INVITE IS WAITING. This branch and the single-assignment one
  // below render NO dropdown, so someone who holds only My Space (or nothing)
  // plus an invite would never see the invite — R16's "the new department
  // should always show in their … drop down list". Both fall through to the
  // dropdown whenever `invites.length > 0`.
  if (assignments.length === 0 && invites.length === 0) {
    return (
      <div className="px-3 py-2" data-testid="dept-switcher">
        <div className="flex items-center gap-2 bg-gray-50 dark:bg-white/5 rounded-lg px-3 py-2 border border-gray-200 dark:border-white/10">
          <BuildingIcon />
          <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-500 dark:text-blue-300 italic`}>No department assigned</p>
        </div>
      </div>
    );
  }

  // Single assignment - show as static label (no dropdown)
  //
  // 🔻 THE `!isAllDepartments` GUARD WAS REMOVED FROM THIS BRANCH ON
  // 2026-08-31. Task 1 added it because a caller could be in All Productions
  // mode and then drop to exactly one assignment (an admin removes their
  // other department between sessions) while the sentinel was still stored:
  // taking this STATIC branch would have rendered the one real department
  // name — a LIE, because every request still carried the sentinel — and
  // offered no way back, since this branch renders no dropdown at all. Task
  // 2 deleted the sentinel from `DepartmentContext.js`, so that state is no
  // longer reachable and the guard is dead. Restored to its pre-mode form.
  if (assignments.length === 1 && invites.length === 0) {
    const a = assignments[0];
    return (
      <div className="px-3 py-2" data-testid="dept-switcher">
        <div className="flex items-center gap-2 bg-gray-50 dark:bg-white/5 rounded-lg px-3 py-2 border border-gray-200 dark:border-white/10">
          <BuildingIcon />
          <div className="min-w-0 flex-1">
            <p className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-800 dark:text-white truncate`} title={label(a)}>
              {label(a)}
            </p>
          </div>
          {collapsedTip && <WaitingDot tip={collapsedTip} testid="dept-waiting-dot-collapsed" />}
          <RoleBadge role={a.role} />
        </div>
      </div>
    );
  }

  // Multiple assignments - dropdown
  //
  // 🆕 CHOOSING A PERSONAL SPACE ASKS FIRST (2026-08-11). Product owner, after
  // testing the landing fix on staging: *"fix my space. lets default to the
  // default department and if the user chooses Myspace give them a pop up
  // warnign to tell them they are leaving their department and how to get
  // back."* The predicate lives in `shouldWarnOnSwitch` — see there for why
  // each of the other transitions is silent.
  // The actual switch, once neither guard below stands in the way. Kept as
  // its own function so BOTH `handleSelect` (a clean click) and
  // `confirmDirtySwitch` (a dirty click the user just confirmed) reach the
  // My Space warning through the SAME path — chaining rather than bypassing
  // it, so a switch that is both dirty AND into a personal space still shows
  // both dialogs in turn instead of the second one going missing.
  const proceedWithSwitch = (dept) => {
    if (shouldWarnOnSwitch(assignments, dept, activeDepartment)) {
      setPendingPersonal(dept);
      return;
    }
    switchDepartment(dept.department_id);
  };

  // 🚨 THE DIRTY-EDIT GUARD RUNS FIRST, BEFORE THE MY SPACE CHECK — owner
  // ruling 2026-08-30 (`DepartmentContext.js`'s `hasDirtyEdit` docblock has
  // the full account). `hasDirtyEdit()` reads a REF, synchronously, so this
  // sees an edit that went dirty on the very last keystroke, not a stale
  // render. Losing typed work is the worse outcome of the two dialogs this
  // component can show, so it is checked first.
  const handleSelect = (dept) => {
    if (hasDirtyEdit?.()) {
      setPendingDirtySwitch(dept);
      setIsOpen(false);
      return;
    }
    proceedWithSwitch(dept);
    setIsOpen(false);
  };

  // ⚠️ CANCEL MUST BE A TRUE NO-OP, FOR BOTH DIALOGS. Nothing above this point
  // has touched the active department or either storage key — the switch
  // happens only inside `proceedWithSwitch`, reached only from a confirm
  // handler — so dismissing either dialog cannot leave the user somewhere
  // they did not ask to be, cannot leave a half-written My Space ack for the
  // next reload to act on, and cannot disturb the dirty edit it was raised to
  // protect: no refetch, no collapse, no clearing of the dirty flag. The user
  // can carry on typing exactly as if the dialog had never appeared.
  const cancelPersonal = () => setPendingPersonal(null);
  const cancelDirtySwitch = () => { setPendingDirtySwitch(null); setPendingDirtyJoin(null); };

  // 🆕 A PENDING ROW: the SAME dirty-edit check as a real row, then the Join
  // prompt — EVERY time (R16). Nothing is switched and nothing is written here;
  // only Join inside the prompt can move the user.
  const handleSelectPending = (invite) => {
    setIsOpen(false);
    if (hasDirtyEdit?.()) {
      setPendingDirtyJoin(invite);
      return;
    }
    setJoinInvite(invite);
  };

  // The department the warning says they are leaving. `activeDepartment` is
  // non-null whenever the dialog can open (you cannot leave nowhere), but it is
  // guarded rather than asserted — an empty string would render a dialog whose
  // whole point is the two names it carries.
  const leavingLabel = activeDepartment ? label(activeDepartment) : '';

  const confirmPersonal = () => {
    switchDepartment(pendingPersonal.department_id);
    setPendingPersonal(null);
  };

  // Continue: the user accepted the loss. Route through `proceedWithSwitch`
  // — never `switchDepartment` directly — so a switch that is ALSO into a
  // personal space still raises that second dialog rather than silently
  // skipping it.
  const confirmDirtySwitch = () => {
    // A dirty click on a PENDING row: Continue opens the Join prompt — it
    // does not join. Discarding the edit is only committed by Join itself.
    if (pendingDirtyJoin) {
      const invite = pendingDirtyJoin;
      setPendingDirtyJoin(null);
      setJoinInvite(invite);
      return;
    }
    const dept = pendingDirtySwitch;
    setPendingDirtySwitch(null);
    proceedWithSwitch(dept);
  };

  return (
    <div className="px-3 py-2 relative" ref={dropdownRef} data-testid="dept-switcher">
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 bg-gray-50 hover:bg-gray-100 dark:bg-white/5 dark:hover:bg-white/10 rounded-lg px-3 py-2 border border-gray-200 dark:border-white/10 transition-colors cursor-pointer"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Switch production department"
        data-testid="dept-switcher-button"
      >
        <BuildingIcon />
        <div className="min-w-0 flex-1 text-left">
          {activeDepartment ? (
            <p className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-800 dark:text-white truncate`} title={label(activeDepartment)}>
              {label(activeDepartment)}
            </p>
          ) : (
            <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-500 dark:text-blue-300 italic`}>Select department...</p>
          )}
        </div>
        {/* 🔻 The `!isAllDepartments` guard went with the sentinel on
            2026-08-31: it existed only because the synthetic All-mode
            `activeDepartment` carried no `role`, so a badge would have
            rendered an empty grey pill. `activeDepartment` is now always a
            real assignment row, which always has one. */}
        {collapsedTip && <WaitingDot tip={collapsedTip} testid="dept-waiting-dot-collapsed" />}
        {activeDepartment && <RoleBadge role={activeDepartment.role} />}
        <ChevronIcon open={isOpen} />
      </button>

      {isOpen && (
        <div
          className="absolute left-3 right-3 mt-1 bg-white dark:bg-[#1a3350] rounded-lg shadow-xl border border-gray-200 dark:border-white/20 z-50 max-h-60 overflow-y-auto"
          role="listbox"
          aria-label="Department assignments"
          data-testid="dept-switcher-dropdown"
        >
          {assignments.map((a) => {
            const isActive = activeDepartment && activeDepartment.department_id === a.department_id;
            // 🆕 Design pass (2026-09-24, Pete: "add a tool tip to the drop
            // down so we can see the full name"): the row's own label AND
            // company line both truncate, so the title carries BOTH — a
            // native `title` on the row shows for the whole hover area even
            // though the two lines inside carry none of their own (this
            // matches the trigger and the single-assignment branch, which
            // already put `title={label(...)}` on their `<p>`).
            const rowTitle = a.company_name ? `${label(a)}\n${a.company_name}` : label(a);
            return (
              <button
                key={a.department_id}
                onClick={() => handleSelect(a)}
                className={optionRowClassName(isActive)}
                role="option"
                aria-selected={isActive}
                title={rowTitle}
                data-testid={`dept-option-${a.department_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-blue-100'} truncate`}>
                    {label(a)}
                  </p>
                  {a.company_name && (
                    <p className="text-[10px] text-gray-500 dark:text-blue-400 truncate">{a.company_name}</p>
                  )}
                </div>
                {waitingCountFor(a.department_id) > 0 && (
                  <WaitingDot
                    tip={rowWaitingTooltip(waitingCountFor(a.department_id))}
                    testid={`dept-waiting-dot-row-${a.department_id}`}
                  />
                )}
                <RoleBadge role={a.role} />
              </button>
            );
          })}
          {/* Pending invites AFTER every real row. `aria-selected` is always
              false: a pending department can never be the active one. */}
          {invites.map((inv) => (
            <button
              key={`pending-${inv.id}`}
              type="button"
              onClick={() => handleSelectPending(inv)}
              className={PENDING_ROW_CLASS}
              role="option"
              aria-selected={false}
              title={`${pendingInviteLabel(inv)}\n${PENDING_INVITE_TEXT}`}
              data-testid={`dept-pending-option-${inv.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-700 dark:text-blue-100 truncate">
                  {pendingInviteLabel(inv)}
                </p>
                {/* 🎨 AMBER, not blue (owner ruling 2026-09-24, small-jobs-backlog.md:
                    "Pete to confirm amber or green" → amber). Blue is already the
                    active-row highlight (line ~491 below, OPTION_ROW_ACTIVE) and the
                    `user` RoleBadge; a pending invite must read as distinct from both,
                    not as "an active-looking row that happens to be unclickable".
                    Shade pair measured against this row's REAL two states (rest =
                    dropdown panel bg; hover = bg-gray-100 light / white/10-over-#1e293b
                    dark — the row never reaches the blue active-row background,
                    aria-selected is always false) in
                    ProductionDepartmentSwitcher.pendingInviteAmber.test.js:
                    amber-700 5.02:1 (rest) / 4.56:1 (hover) light,
                    amber-400 8.76:1 (rest) / 6.44:1 (hover) dark — all clear WCAG
                    1.4.3's 4.5:1 text minimum. amber-500/600 measured FAIL in light
                    (2.15:1 / 2.89:1 on hover) — do not "simplify" to a single shade
                    across both themes. */}
                <p className="text-[11px] italic text-amber-700 dark:text-amber-400 truncate">
                  {PENDING_INVITE_TEXT}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {joinInvite && (
        <JoinDepartmentDialog invite={joinInvite} onClose={() => setJoinInvite(null)} returnFocusRef={triggerRef} />
      )}

      {/*
        🔑 THE COPY IS THE DELIVERABLE HERE, NOT THE MODAL. The owner asked for
        a warning that says two things — that they are leaving their department,
        AND how to get back — so a bare "Are you sure?" does not satisfy the
        instruction. It therefore NAMES the department being left and NAMES the
        control that returns them, and it explains what My Space IS, because the
        people most likely to land in it by accident are the ones who have never
        been told.

        No colour classes of our own: `ConfirmDialog` owns the chrome and its
        buttons come from `constants/buttons.js`, so there is nothing here for
        `index.css`'s `!important` dark-mode overrides to beat, and dark mode is
        inherited rather than re-declared.

        `variant` is left at its default — this is a NAVIGATION choice, not a
        destructive one, and `danger` red would overstate a switch the user can
        undo in one click.
      */}
      {/* Portalled (`toBody`): the sidebar wraps this component in a `z-30`
          stacking context that the page title bar out-paints — measured, see
          `toBody` in `JoinDepartmentDialog.js`. */}
      {toBody(<ConfirmDialog
        open={!!pendingPersonal}
        title="Switch to My Space?"
        message={leavingLabel
          ? `My Space is your own private workspace. While you are in it you will not see the orders, budgets or approvals for ${leavingLabel} — and nobody else can see what you put there. To come back, open this same production switcher and choose ${leavingLabel} again.`
          : ''}
        confirmLabel="Go to My Space"
        cancelLabel="Stay here"
        onConfirm={confirmPersonal}
        onCancel={cancelPersonal}
      />)}

      {/*
        🔑 HIS WORDING, VERBATIM, NOT HOUSE STYLE — owner, 2026-08-30:
        *"'You have unsaved changes — they'll be lost. Continue / Cancel.' is a
        good idea, lets have the warning"*. `variant="danger"` because, unlike
        the My Space dialog above (a navigation choice, reversible in one
        click), this one is genuinely destructive: Continue discards typed
        work with no undo. See `DepartmentContext.js`'s `hasDirtyEdit` for why
        this is the ONE place in the order pages a dirty edit does not simply
        block or defer, and why tab-refocus is deliberately NOT wired to this
        dialog — that trigger stays silent, per `usePollingRefresh`'s existing
        `paused` rule, because nobody asked to switch anything by alt-tabbing
        back.
      */}
      {toBody(<ConfirmDialog
        open={!!pendingDirtySwitch || !!pendingDirtyJoin}
        title={UNSAVED_SWITCH_TITLE}
        message={UNSAVED_SWITCH_MESSAGE}
        confirmLabel="Continue"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={confirmDirtySwitch}
        onCancel={cancelDirtySwitch}
      />)}
    </div>
  );
}
